import { useState, useRef, useEffect } from "react";
import { QrCode, Download, Printer, Plus, Minus, Table2, Settings, Eye, MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { API_URL } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { toast } from "sonner";

// QR Code generator using API
const generateQRCodeURL = (data, size = 200) => {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}&bgcolor=ffffff&color=000000&margin=10`;
};

const RESTAURANT_LOGO = "https://customer-assets.emergentagent.com/job_taco-tracker-4/artifacts/49jtitvp_2NARANJAlogo%20ordenado%20definitivo.png";

export default function AdminQRMesas() {
  const [numTables, setNumTables] = useState(10);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [restaurantName, setRestaurantName] = useState("Conexo POS");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const printRef = useRef();

  // Load branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/branches`, { withCredentials: true });
        const activeBranches = res.data.filter(b => b.active);
        setBranches(activeBranches);
        if (activeBranches.length > 0) {
          setSelectedBranch(activeBranches[0].branch_id);
          // Set name based on branch if needed, or keep generic
        }
      } catch (error) {
        console.error("Error loading branches");
      }
    };
    fetchBranches();
  }, []);

  // Base URL for the app
  const baseUrl = window.location.origin;

  // Generate QR URL for a specific table
  // Generate QR URL for a specific table
  const getTableQRUrl = (tableNum) => {
    let url = `${baseUrl}/menu?mesa=${tableNum}`;
    if (selectedBranch) {
      url += `&sucursal=${selectedBranch}`;
    }
    return url;
  };

  // Print a single QR
  const printSingleQR = (tableNum) => {
    const qrUrl = getTableQRUrl(tableNum);
    const printWindow = window.open('', '_blank', 'width=400,height=600');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Mesa ${tableNum} - ${restaurantName}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background: white;
            }
            .qr-card {
              width: 100mm;
              padding: 10mm;
              text-align: center;
              border: 3px solid #000;
              border-radius: 10px;
            }
            .logo {
              width: 60px;
              height: 60px;
              margin: 0 auto 10px;
              border-radius: 8px;
              object-fit: contain;
            }
            .restaurant-name {
              font-size: 18px;
              font-weight: bold;
              color: #000;
              margin-bottom: 5px;
            }
            .table-badge {
              display: inline-block;
              background: #000;
              color: #fff;
              padding: 8px 20px;
              border-radius: 20px;
              font-size: 24px;
              font-weight: bold;
              margin: 10px 0;
            }
            .qr-code {
              margin: 15px auto;
            }
            .qr-code img {
              width: 150px;
              height: 150px;
            }
            .instructions {
              font-size: 14px;
              color: #333;
              margin-top: 10px;
            }
            .scan-text {
              font-size: 16px;
              font-weight: bold;
              color: #000;
              margin-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .qr-card { border: 3px solid #000; }
            }
          </style>
        </head>
        <body>
          <div class="qr-card">
            <img src="${RESTAURANT_LOGO}" alt="Logo" class="logo" />
            <div class="restaurant-name">${restaurantName}</div>
            <div class="table-badge">MESA ${tableNum}</div>
            <div class="qr-code">
              <img src="${generateQRCodeURL(qrUrl, 300)}" alt="QR Code" />
            </div>
            <div class="scan-text">📱 ESCANEA Y PIDE</div>
            <div class="instructions">
              Escanea el código QR para<br/>
              ver el menú y hacer tu pedido
            </div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() { window.close(); };
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Print all QR codes
  const printAllQRs = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');

    const qrCards = Array.from({ length: numTables }, (_, i) => {
      const tableNum = i + 1;
      const qrUrl = getTableQRUrl(tableNum);
      return `
        <div class="qr-card">
          <img src="${RESTAURANT_LOGO}" alt="Logo" class="logo" />
          <div class="restaurant-name">${restaurantName}</div>
          <div class="table-badge">MESA ${tableNum}</div>
          <div class="qr-code">
            <img src="${generateQRCodeURL(qrUrl, 200)}" alt="QR Code" />
          </div>
          <div class="scan-text">📱 ESCANEA Y PIDE</div>
          <div class="instructions">Escanea para ver el menú</div>
        </div>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Mesas - ${restaurantName}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              background: white;
            }
            .grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 20px;
            }
            .qr-card {
              padding: 15px;
              text-align: center;
              border: 2px solid #000;
              border-radius: 10px;
              page-break-inside: avoid;
            }
            .logo {
              width: 40px;
              height: 40px;
              margin: 0 auto 5px;
              border-radius: 5px;
              object-fit: contain;
            }
            .restaurant-name {
              font-size: 14px;
              font-weight: bold;
              color: #000;
            }
            .table-badge {
              display: inline-block;
              background: #000;
              color: #fff;
              padding: 5px 15px;
              border-radius: 15px;
              font-size: 16px;
              font-weight: bold;
              margin: 8px 0;
            }
            .qr-code img {
              width: 120px;
              height: 120px;
            }
            .scan-text {
              font-size: 12px;
              font-weight: bold;
              margin-top: 5px;
            }
            .instructions {
              font-size: 10px;
              color: #666;
            }
            @media print {
              .grid {
                grid-template-columns: repeat(2, 1fr);
              }
            }
          </style>
        </head>
        <body>
          <div class="grid">
            ${qrCards}
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 1000);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Download QR as image
  const downloadQR = async (tableNum) => {
    const qrUrl = getTableQRUrl(tableNum);
    const imageUrl = generateQRCodeURL(qrUrl, 500);

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `QR_Mesa_${tableNum}_${restaurantName.replace(/\s/g, '_')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`QR Mesa ${tableNum} descargado`);
    } catch (error) {
      toast.error("Error al descargar el QR");
    }
  };

  return (
    <AdminLayout title="QR Mesas">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-yellow-500 flex items-center gap-2">
              <QrCode className="w-7 h-7" />
              CÓDIGOS QR PARA MESAS
            </h1>
            <p className="text-gray-400">
              Genera códigos QR para que los clientes pidan desde su mesa
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={printAllQRs}
              className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir Todos
            </Button>
          </div>
        </div>

        {/* Settings Card */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-yellow-500" />
              Configuración
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-gray-400 text-sm">Número de mesas:</label>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-8 p-0 border-gray-700"
                    onClick={() => setNumTables(Math.max(1, numTables - 1))}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    value={numTables}
                    onChange={(e) => setNumTables(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-16 text-center bg-gray-800 border-gray-700 text-white"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-8 p-0 border-gray-700"
                    onClick={() => setNumTables(numTables + 1)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>


              <div className="flex items-center gap-2">
                <label className="text-gray-400 text-sm">Sucursal:</label>
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="w-[180px] bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(b => (
                      <SelectItem key={b.branch_id} value={b.branch_id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-gray-400 text-sm">Nombre restaurante:</label>
                <Input
                  value={restaurantName}
                  onChange={(e) => setRestaurantName(e.target.value)}
                  className="w-40 bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* QR Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Array.from({ length: numTables }, (_, i) => {
            const tableNum = i + 1;
            const qrUrl = getTableQRUrl(tableNum);

            return (
              <Card key={tableNum} className="bg-gray-900 border-gray-800 hover:border-yellow-500 transition-colors">
                <CardContent className="p-4 text-center">
                  <div className="bg-white rounded-lg p-3 mb-3">
                    <img
                      src={generateQRCodeURL(qrUrl, 200)}
                      alt={`QR Mesa ${tableNum}`}
                      className="w-full aspect-square"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Table2 className="w-5 h-5 text-yellow-500" />
                    <span className="font-heading font-bold text-white text-xl">Mesa {tableNum}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-gray-700 text-gray-300 hover:text-white"
                      onClick={() => {
                        setSelectedTable(tableNum);
                        setShowPreview(true);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-gray-700 text-gray-300 hover:text-white"
                      onClick={() => printSingleQR(tableNum)}
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-gray-700 text-gray-300 hover:text-white"
                      onClick={() => downloadQR(tableNum)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Instructions */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-white">📋 Instrucciones</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-400 space-y-2">
            <p>1. <strong className="text-white">Configura</strong> el número de mesas de tu local</p>
            <p>2. <strong className="text-white">Imprime</strong> los códigos QR individualmente o todos a la vez</p>
            <p>3. <strong className="text-white">Pega</strong> cada QR en la mesa correspondiente (se recomienda plastificar)</p>
            <p>4. Los clientes <strong className="text-white">escanean</strong> el QR, ven el menú y hacen su pedido</p>
            <p>5. El pedido llega a <strong className="text-white">cocina</strong> con el número de mesa asignado automáticamente</p>
          </CardContent>
        </Card>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-yellow-500 flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              Vista Previa - Mesa {selectedTable}
            </DialogTitle>
          </DialogHeader>

          {selectedTable && (
            <div className="text-center py-4">
              <div className="bg-white rounded-xl p-6 inline-block">
                <img
                  src={RESTAURANT_LOGO}
                  alt="Logo"
                  className="w-16 h-16 mx-auto mb-2 rounded-lg object-contain"
                />
                <h3 className="text-black font-bold text-lg">{restaurantName}</h3>
                <div className="bg-black text-white px-6 py-2 rounded-full font-bold text-xl my-3 inline-block">
                  MESA {selectedTable}
                </div>
                <img
                  src={generateQRCodeURL(getTableQRUrl(selectedTable), 300)}
                  alt="QR Code"
                  className="w-48 h-48 mx-auto my-3"
                />
                <p className="text-black font-bold text-lg">📱 ESCANEA Y PIDE</p>
                <p className="text-gray-600 text-sm mt-1">
                  Escanea el código QR para<br />
                  ver el menú y hacer tu pedido
                </p>
              </div>

              <div className="mt-4 flex gap-2 justify-center">
                <Button
                  onClick={() => printSingleQR(selectedTable)}
                  className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadQR(selectedTable)}
                  className="border-gray-700 text-gray-300"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout >
  );
}
